/**
 * Routes the legacy `useRouteStopOperations.unassignStops` surface through
 * v2 `executeOperation`. Keeps a thin JS wrapper so existing callers
 * (`StopDetailsActions`, `WorkOrderAccordion`, `RoutingActionsProvider`)
 * can stay unchanged. Other operations (assign / move-between-days /
 * resequence) are exposed only through the dialogs and the
 * `useRouteOperation` hook directly — see the bulk dialogs for examples.
 */
import { useCallback, useMemo } from 'react'

import { routeOpFailureMessage } from 'components/route-manager/common/RouteOpResultToast'
import { useRouteOperation } from 'hooks/useRouteOperation'
import { formatDateToBEFormatDateFns } from 'utils/date'

const useRouteStopOperations = () => {
  const { run: runRouteOperation, isRunning: isV2Running } = useRouteOperation()

  /**
   * Always affects daily + master + future daily. `configuredServiceEventIds[i]` marks `workOrderIds[i]`
   * recurring and a falsy one marks it on-request; without `routeSlotIds[i]` v3 falls back to v1.
   */
  const unassignStops = useCallback(
    async ({ routeId, serviceDate, workOrderIds, configuredServiceEventIds = [], routeSlotIds = [] }) => {
      const serviceDateStr = formatDateToBEFormatDateFns(serviceDate instanceof Date ? serviceDate : new Date(serviceDate))
      const stops = workOrderIds.map((woId, i) => {
        const cse = configuredServiceEventIds[i] || null
        const routeSlotId = routeSlotIds[i] || null
        return cse
          ? { kind: 'daily', workOrderId: woId, configuredServiceEventId: cse, routeSlotId }
          : { kind: 'on-request', workOrderId: woId, configuredServiceEventId: null, routeSlotId }
      })

      const result = await runRouteOperation({
        kind: 'unassign',
        routeId,
        serviceDate: serviceDateStr,
        stops,
        includeMaster: true,
      })
      if (!result.success) {
        throw new Error(routeOpFailureMessage(result, 'Unassign failed.'))
      }
    },
    [runRouteOperation]
  )

  return useMemo(() => ({ unassignStops, isLoading: isV2Running }), [unassignStops, isV2Running])
}

export default useRouteStopOperations
